import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Camera, CheckCircle, ArrowRight, Shield, Star,
  Award, Target, MessageCircle, Eye, Image, MapPin,
  TrendingUp, Users, Clock, Zap, ChevronRight,
  GraduationCap, Sparkles
} from "lucide-react";
import { ContactSection, SectionDivider, WA_LINK, CALENDLY } from "@/Layout";
import { CrossSellingGift, CrossSellingProducts } from "@/components/CrossSelling";

const PayPalLogo = ({ className = "w-4 h-3" }) => (
  <svg className={className} viewBox="0 0 101 32" fill="currentColor">
    <path d="M12.2 4.6h-7c-.5 0-.9.3-1 .8L1.5 24c-.1.4.2.7.6.7h3.4c.5 0 .9-.3 1-.8l.8-5.1c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C16.8 5.3 14.8 4.6 12.2 4.6z"/>
    <path d="M39.6 4.6h-7c-.5 0-.9.3-1 .8l-2.7 18.6c-.1.4.2.7.6.7h3.6c.3 0 .6-.2.7-.6l.8-5.3c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C44.2 5.3 42.2 4.6 39.6 4.6z"/>
  </svg>
);

/* ========================================
   PAGINA UNIFICATA: Formazione + Virtual Tour
   Route: /deducibile100
   ======================================== */

const DeducibilePage = () => {
  const portfolio = [
    { name: "Open Studio", image: "https://lh3.googleusercontent.com/pw/AP1GczNH_iDl6CjdaFxtYvOPTlc9drJTLycSzz_UltrlEvezGR7VyYRwTE9LSywJTeJQDEAELLSX4aRwjmC4MITAY7krtmYZoQovFS1V3qQ93lyGV5gzSeMK=w800-h400-no", type: "Studio" },
    { name: "Poggio alle Lame", image: "https://lh3.googleusercontent.com/pw/AP1GczPv9Rr8KS1A-8nRipqV6Xhfbx0xJeSqBIpmNbO1gvOucpFVeobIVwuK78B-0ilZ72MZUYqhKxGEQE_txDp4ku3jojpVYyT685mHSidAQgws80o8--xb=w800-h400-no", type: "Agriturismo" },
    { name: "Quattropuntozero", image: "https://lh3.googleusercontent.com/pw/AP1GczN_0D2frmiV9WS41RNSEhquWFwTA6dG-BPSQYj8n8G57GgS0isNdBoIDoGvJtEKRSmT3uGqDYQQQ4eQVp7zqdIkRrng0pUFWvH-5ALCEgFYuO8XPTwn=w800-h400-no", type: "Locale" },
    { name: "Centro Medico Arnolfo", image: "https://lh3.googleusercontent.com/pw/AP1GczM1ChmyPTVlm6b93mXdQPYC8b9XrE83KEehiDgATIz5l-m2JHAls9r1NA3p8KjtbpHaGLqx3bJYQV2eJ0nmU_qL1BWifRfwqrhju7tlMQqkmWE2OU4w=w800-h400-no", type: "Centro Medico" },
  ];

  return (
    <div className="min-h-screen bg-[#0c1220]" data-testid="deducibile-page">

      {/* ======================== HERO ======================== */}
      <section className="relative overflow-hidden" data-testid="deducibile-hero">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-20 w-96 h-96 rounded-full bg-[#4285F4]/10 blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-64 h-64 rounded-full bg-[#34A853]/8 blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FBBC04]/5 blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 py-16 md:py-24 relative text-center">
          <Badge className="bg-white/10 text-white border-white/20 mb-6 rounded-full px-6 py-2 text-sm font-bold" data-testid="deducibile-badge">
            <CheckCircle className="w-4 h-4 mr-2" />
            FORMAZIONE + VIRTUAL TOUR
          </Badge>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6" data-testid="deducibile-title">
            <span className="text-[#93c5fd]">100%</span> Deducibile
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
            Formazione sulla tua scheda Google + Virtual Tour 360° per la tua attivita'.
            <strong className="text-white"> Acquistali entrambi</strong> con un pacchetto e risparmi.
          </p>

          <div className="flex flex-wrap justify-center gap-6 mb-10">
            <div className="text-center">
              <p className="text-[#93c5fd] text-4xl md:text-5xl font-black">100%</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-1">Deducibile</p>
            </div>
            <div className="text-center">
              <p className="text-white text-4xl md:text-5xl font-black">TAEG 0%</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-1">Senza interessi</p>
            </div>
            <div className="text-center">
              <p className="text-white text-4xl md:text-5xl font-black">3 rate</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-1">PayPal</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" className="bg-white text-[#0c1220] hover:bg-gray-200 rounded-full px-10 font-bold text-base"
              onClick={() => document.getElementById('bundle-acquistali')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-scroll-bundle">
              <Sparkles className="w-5 h-5 mr-2" /> Scopri i Bundle
            </Button>
            <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-10"
              onClick={() => window.open(WA_LINK + '?text=' + encodeURIComponent('Ciao Riccardo! Vorrei informazioni sulla formazione + virtual tour 100% deducibile.'), '_blank')}
              data-testid="hero-wa-cta">
              <MessageCircle className="w-5 h-5 mr-2" /> Scrivimi su WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* ======================== STRISCIA PAYPAL ======================== */}
      <section className="bg-[#003087] py-3">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap items-center justify-center gap-4 md:gap-8">
          <div className="flex items-center gap-2">
            <PayPalLogo className="w-5 h-4 text-white" />
            <span className="text-white font-bold text-sm">3 RATE PAYPAL</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-white" />
            <span className="text-white font-bold text-sm">100% DEDUCIBILE</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-white" />
            <span className="text-white font-bold text-sm">TAEG 0%</span>
          </div>
        </div>
      </section>

      {/* ======================== BUNDLE "ACQUISTALI ENTRAMBI" ======================== */}
      {/* Cornice arcobaleno Google / dorata */}
      <section id="bundle-acquistali" className="py-16 bg-[#0a0e1a]" data-testid="bundle-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#FBBC04]" />
              <span className="text-[#FBBC04] font-black text-sm uppercase tracking-widest">Acquistali Entrambi</span>
              <Sparkles className="w-5 h-5 text-[#FBBC04]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white font-google mb-3">
              Formazione + <span className="text-[#FBBC04]">Pacchetto</span> = Risparmio
            </h2>
            <p className="text-gray-400 max-w-lg mx-auto">
              Abbina la formazione a un pacchetto di assistenza. Piu' sali, piu' vale — fino a IN REGALO.
            </p>
          </div>

          {/* === CORNICE ARCOBALENO GOOGLE === */}
          <style>{`
            @keyframes rainbow-glow {
              0%, 100% { filter: blur(20px) brightness(1); }
              50% { filter: blur(28px) brightness(1.3); }
            }
            .rainbow-frame-glow {
              animation: rainbow-glow 3s ease-in-out infinite;
            }
          `}</style>
          <div className="relative" data-testid="bundle-rainbow-frame">
            {/* Glow layer */}
            <div className="absolute -inset-1 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] rounded-3xl rainbow-frame-glow opacity-40"></div>
            {/* Border layer */}
            <div className="relative p-[3px] rounded-3xl bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853]">
              <div className="bg-[#0c1220] rounded-[21px] p-6 md:p-10">

                <div className="space-y-4">
                  {/* Start + Formazione = €199 */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white/5 rounded-2xl px-5 py-4 border border-[#EA4335]/20 hover:border-[#EA4335]/50 transition-colors group" data-testid="bundle-start">
                    <div className="flex items-center gap-3 mb-2 sm:mb-0">
                      <span className="w-3 h-3 rounded-full bg-[#EA4335] flex-shrink-0"></span>
                      <div>
                        <span className="text-white font-bold text-base">Start + Formazione</span>
                        <p className="text-gray-500 text-xs">Verifica scheda + formazione completa</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 line-through text-sm">€398</span>
                      <span className="text-3xl font-black text-[#EA4335]">€199</span>
                    </div>
                  </div>

                  {/* Plus + Formazione = €149 */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white/5 rounded-2xl px-5 py-4 border border-[#FBBC04]/20 hover:border-[#FBBC04]/50 transition-colors group" data-testid="bundle-plus">
                    <div className="flex items-center gap-3 mb-2 sm:mb-0">
                      <span className="w-3 h-3 rounded-full bg-[#FBBC04] flex-shrink-0"></span>
                      <div>
                        <span className="text-white font-bold text-base">Plus + Formazione</span>
                        <p className="text-gray-500 text-xs">Sospensione, recensioni + formazione</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 line-through text-sm">€598</span>
                      <span className="text-3xl font-black text-[#FBBC04]">€149</span>
                    </div>
                  </div>

                  {/* Rooftop + Formazione = IN REGALO */}
                  <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between bg-[#34A853]/10 rounded-2xl px-5 py-5 border-2 border-[#34A853]/40 hover:border-[#34A853]/70 transition-colors group" data-testid="bundle-rooftop">
                    <div className="absolute -top-3 right-4 bg-[#34A853] text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider">In Regalo</div>
                    <div className="flex items-center gap-3 mb-2 sm:mb-0">
                      <span className="w-3 h-3 rounded-full bg-[#34A853] flex-shrink-0"></span>
                      <div>
                        <span className="text-white font-bold text-base">Rooftop + Formazione</span>
                        <p className="text-gray-500 text-xs">TUTTO incluso + formazione IN REGALO</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 line-through text-sm">€898</span>
                      <span className="text-3xl font-black text-[#34A853]">IN REGALO</span>
                    </div>
                  </div>
                </div>

                {/* CTA Bundle */}
                <div className="mt-8 text-center">
                  <button
                    onClick={() => window.open(`https://wa.me/+393273839782?text=${encodeURIComponent('Ciao Riccardo! Voglio un pacchetto + Formazione! Quale mi consigli?')}`, '_blank')}
                    className="w-full sm:w-auto px-10 py-4 rounded-full font-bold text-base bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] text-white hover:opacity-90 transition-opacity"
                    data-testid="bundle-cta"
                  >
                    Scegli il tuo Bundle
                  </button>
                  <p className="text-gray-500 text-xs mt-3">
                    <PayPalLogo className="w-4 h-3 inline mr-1 text-gray-400" />
                    Tutti i bundle pagabili in 3 rate PayPal · TAEG 0%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Nota Virtual Tour */}
          <div className="mt-6 bg-white/5 border border-white/10 rounded-xl p-4 text-center">
            <p className="text-gray-400 text-sm">
              <Camera className="w-4 h-4 inline mr-1 text-gray-500" />
              I Virtual Tour 360° sono disponibili esclusivamente con i pacchetti <strong className="text-white">Start</strong>, <strong className="text-white">Plus</strong> e <strong className="text-white">Rooftop</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* ======================== VIRTUAL TOUR 360° ======================== */}
      <section className="py-16 bg-[#0a0a0a] relative overflow-hidden" data-testid="vt360-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block mb-5 relative" data-testid="gopro-channel-logo">
              <div className="bg-gray-900 border-2 border-gray-800/40 rounded-2xl p-3 shadow-xl shadow-gray-800/10">
                <img src="https://customer-assets.emergentagent.com/job_9aee3f9a-9ba9-47c5-9ef1-87f76cda4250/artifacts/b4icib11_image.png"
                  alt="GoPro MAX 360°" className="w-20 h-20 md:w-24 md:h-24 object-contain" />
              </div>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[9px] font-black px-3 py-0.5 rounded-full tracking-widest uppercase">
                Powered by
              </div>
            </div>
            <h2 className="font-typewriter text-3xl md:text-4xl text-gray-300 mb-2">Virtual Tour 360°</h2>
            <p className="text-gray-500 text-sm tracking-widest uppercase mb-4">Incluso nei pacchetti Start, Plus e Rooftop</p>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Fai <strong className="text-white">scoprire</strong> la <strong className="text-white">TUA</strong> attivita'
              ai clienti prima ancora che varchino la soglia.
            </p>
          </div>

          {/* Behind the Scenes */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] group" data-testid="vt360-action-photo">
              <img src="https://customer-assets.emergentagent.com/job_031c0675-bd53-49c3-b1d2-b66f6ab51727/artifacts/hke4h6ow_WhatsApp%20Image%202026-03-18%20at%2012.46.35.jpeg" alt="GoPro MAX 360°" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-white font-bold text-xl mb-1">GoPro MAX 360°</h3>
                <p className="text-gray-300 text-sm">16.6MP · 5.6K30 · HyperSmooth</p>
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <h3 className="font-typewriter text-2xl text-gray-300 mb-4">L'attrezzatura fa la differenza</h3>
                <div className="space-y-4">
                  {[
                    { icon: Camera, title: "GoPro MAX 360°", desc: "Cattura ogni angolo della TUA attivita' in qualita' cinematografica", color: "text-gray-400" },
                    { icon: Eye, title: "Esperienza Immersiva", desc: "I TUOI clienti 'entrano' nel locale — risultati sorprendenti", color: "text-gray-400" },
                    { icon: TrendingUp, title: "Pubblicazione Google Street View", desc: "La TUA attivita' visibile a milioni di persone su Maps", color: "text-gray-400" },
                    { icon: Camera, title: "3 foto sferiche in REGALO", desc: "Incluse con ogni pacchetto — valorizza i tuoi spazi", color: "text-[#ff6b35]" },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <div className={`w-10 h-10 ${item.color === 'text-[#ff6b35]' ? 'bg-[#ff6b35]/20' : 'bg-white/5'} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <item.icon className={`w-5 h-5 ${item.color}`} />
                      </div>
                      <div>
                        <p className={`${item.color === 'text-[#ff6b35]' ? 'text-[#ff6b35]' : 'text-white'} font-bold text-sm`}>{item.title}</p>
                        <p className="text-gray-500 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Portfolio Gallery */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="vt360-gallery">
            {portfolio.map((item, i) => (
              <div key={i} className="group relative rounded-2xl overflow-hidden aspect-[4/3] cursor-pointer">
                <img src={item.image} alt={`Virtual Tour ${item.name}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <Badge className="bg-gray-800/80 text-white border-0 text-xs mb-1">{item.type}</Badge>
                  <p className="text-white font-bold text-sm">{item.name}</p>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 bg-gray-800/80 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">360</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================== IL PERCORSO ======================== */}
      <section className="py-16 bg-[#0c1220]">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-black text-white font-google text-center mb-3">
            Cosa succede quando <span className="text-[#4285F4]">investi</span> in te
          </h2>
          <p className="text-gray-500 text-sm text-center mb-10 max-w-lg mx-auto">
            Dalla formazione alla consapevolezza digitale, fino alla detrazione fiscale.
          </p>
          <div className="space-y-0">
            {[
              { step: "1", title: "Fai formazione con Riccardo", desc: "Impari a gestire la tua scheda Google Business Profile. Niente teoria inutile: solo pratica, sul TUO profilo reale.", accent: "bg-[#4285F4]" },
              { step: "2", title: "Diventi consapevole e autonomo", desc: "Sai rispondere alle recensioni, ottimizzare foto, gestire orari e categorie.", accent: "bg-[#34A853]" },
              { step: "3", title: "Paghi in 3 comode rate PayPal", desc: "Nessun anticipo pesante. TAEG 0%, zero interessi. PayPal divide il costo in 3 rate automatiche.", accent: "bg-[#FBBC04]" },
              { step: "4", title: "Tra 12 mesi detrai il 100%", desc: "La formazione professionale e' deducibile al 100% per P.IVA e imprese. Quei soldi tornano. Formazione a costo ZERO.", accent: "bg-[#EA4335]" },
            ].map((s) => (
              <div key={s.step} className="flex gap-4 pb-8 relative">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 ${s.accent} rounded-full flex items-center justify-center text-white font-black text-sm flex-shrink-0 z-10`}>{s.step}</div>
                  {s.step !== "4" && <div className="w-0.5 h-full bg-white/10 mt-1"></div>}
                </div>
                <div className="pt-1.5">
                  <p className="font-bold text-white text-base mb-1">{s.title}</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================== COSA IMPARI ======================== */}
      <section className="py-16 bg-[#0a0e1a]">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-black text-white font-google text-center mb-3">
            Cosa impari con la <span className="text-[#4285F4]">formazione</span>
          </h2>
          <p className="text-gray-500 text-sm text-center mb-10 max-w-md mx-auto">
            Formazione pratica, sul TUO profilo. Niente corsi preregistrati.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Shield, title: "Recupero Scheda Sospesa", desc: "Ti guido passo dopo passo nel processo di recupero. Ho risolto centinaia di casi.", color: "text-[#EA4335]", bg: "bg-[#EA4335]/10" },
              { icon: Star, title: "Gestione Recensioni", desc: "Ti insegno a rispondere alle recensioni in modo professionale. Ogni risposta e' un'opportunita'.", color: "text-[#FBBC04]", bg: "bg-[#FBBC04]/10" },
              { icon: Image, title: "Fotografia Professionale", desc: "7.000 foto pubblicate su Google Maps. Scatto foto ottimizzate per la tua scheda.", color: "text-[#4285F4]", bg: "bg-[#4285F4]/10" },
              { icon: Camera, title: "Virtual Tour 360°", desc: "Con la GoPro MAX creo tour virtuali immersivi. Il 67% degli utenti preferisce attivita' con tour 360°.", color: "text-white", bg: "bg-white/10" },
              { icon: Target, title: "Ottimizzazione Profilo", desc: "Analizzo e ottimizzo ogni aspetto della tua scheda: categorie, attributi, orari. Risultati in 30 giorni.", color: "text-[#34A853]", bg: "bg-[#34A853]/10" },
              { icon: TrendingUp, title: "Analisi Competitor", desc: "Studio i tuoi concorrenti su Google Maps e ti mostro come superarli in visibilita'.", color: "text-purple-400", bg: "bg-purple-500/10" },
            ].map((s) => (
              <div key={s.title} className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-colors">
                <div className={`w-12 h-12 ${s.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <s.icon className={`w-6 h-6 ${s.color}`} />
                </div>
                <h3 className="text-base font-bold text-white mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================== DETTAGLI FISCALI ======================== */}
      <section className="py-16 bg-[#0c1220] border-y border-[#4285F4]/20" data-testid="deducibile-fiscali">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-[#4285F4] text-white inline-flex items-center gap-2 px-6 py-2 rounded-full font-bold text-sm mb-6">
            <CheckCircle className="w-4 h-4" /> DETTAGLI FISCALI
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white font-google mb-8">
            Formazione <span className="text-[#4285F4]">100% Deducibile</span>
          </h2>

          <div className="bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10 max-w-2xl mx-auto mb-8 text-left">
            <p className="text-white text-lg font-bold mb-5 text-center">Chi puo' dedurre e quanto:</p>
            <div className="space-y-4">
              {[
                { tag: "P.IVA", title: "Professionisti e Lavoratori Autonomi", desc: "Corsi, aggiornamento, master: deducibili al 100% fino a 10.000 euro/anno" },
                { tag: "SRL", title: "Imprese", desc: "Formazione per dipendenti: deducibile dal reddito d'impresa senza limiti" },
                { tag: "4.0", title: "Formazione 4.0 — Tecnologie Digitali", desc: "Credito d'imposta fino al 70% delle spese sostenute per formazione digitale" },
              ].map((item) => (
                <div key={item.tag} className="flex items-start gap-3 bg-[#4285F4]/10 rounded-xl p-4 border border-[#4285F4]/20">
                  <div className="w-8 h-8 bg-[#4285F4] rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{item.tag}</div>
                  <div>
                    <p className="font-bold text-white text-sm">{item.title}</p>
                    <p className="text-gray-400 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 bg-[#FBBC04]/10 border border-[#FBBC04]/20 rounded-xl p-4">
              <p className="text-[#FBBC04] text-xs font-bold mb-1">Requisiti:</p>
              <p className="text-gray-400 text-xs">La formazione deve essere inerente all'attivita' lavorativa. Conserva la fattura e la prova del pagamento.</p>
              <p className="text-gray-500 text-[10px] mt-1">Nota: il regime forfettario non consente la deduzione analitica dei costi formativi.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================== CHI SONO ======================== */}
      <section className="relative overflow-hidden bg-[#0a0e1a]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-96 h-96 rounded-full bg-[#ff6b35]/30 blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 py-16 relative">
          <div className="max-w-2xl">
            <Badge className="bg-[#ff6b35]/20 text-[#ff6b35] border-[#ff6b35]/30 mb-6 rounded-full px-4 py-1.5">
              <Camera className="w-4 h-4 mr-2" />
              Google Street View Trusted dal 2016
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black text-white font-google leading-tight mb-4">
              Riccardo <span className="text-[#ff6b35]">Rossi</span>
            </h2>
            <p className="text-white/40 text-sm tracking-widest uppercase mb-6">Il tuo esperto Google Business Profile</p>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
              Non vendo corsi. Non sono un chatbot. Sono un professionista che da <strong className="text-white">9 anni</strong> risolve problemi reali per attivita' reali su Google Maps.
            </p>
            <div className="grid grid-cols-3 gap-4 mb-8" data-testid="chi-sono-stats">
              {[
                { num: "9", unit: "anni", label: "Sul campo", color: "text-[#ff6b35]" },
                { num: "7.000", unit: "foto", label: "Su Google Maps", color: "text-[#4285F4]" },
                { num: "52M", unit: "views", label: "Visualizzazioni", color: "text-[#34A853]" },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <p className={`text-3xl md:text-4xl font-black ${s.color}`}>{s.num}</p>
                  <p className="text-white/30 text-[10px] uppercase tracking-wider">{s.unit}</p>
                  <p className="text-white/40 text-xs mt-1 hidden md:block">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ======================== PERCHE' ME ======================== */}
      <section className="py-16 bg-[#0c1220]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-black text-white font-google text-center mb-10">
            Perche' scegliere <span className="text-[#ff6b35]">me</span>
          </h2>
          <div className="space-y-4">
            {[
              { icon: Award, label: "Certificazione Google", text: "Fotografo Certificato Google Street View dal 2016. Una certificazione verificata da Google.", accent: "text-[#4285F4]" },
              { icon: Eye, label: "52 Milioni di Views", text: "Le mie foto su Google Maps hanno totalizzato 52 milioni di visualizzazioni. 9 anni di lavoro costante.", accent: "text-[#34A853]" },
              { icon: Users, label: "Formazione Diretta", text: "Parlo con te, non con un modulo. Niente chatbot, niente segretarie. WhatsApp, telefono, videochiamata.", accent: "text-[#ff6b35]" },
              { icon: MapPin, label: "Risultati Misurabili", text: "Ti mostro i numeri PRIMA e DOPO. Visualizzazioni, chiamate, richieste indicazioni. Dati reali.", accent: "text-[#EA4335]" },
              { icon: Zap, label: "Velocita'", text: "Problemi urgenti? Scheda sospesa? Rispondo entro 24 ore. Ogni giorno offline e' fatturato perso.", accent: "text-[#FBBC04]" },
            ].map((r) => (
              <div key={r.label} className="flex items-start gap-4 p-5 bg-white/5 rounded-2xl border border-white/10">
                <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <r.icon className={`w-5 h-5 ${r.accent}`} />
                </div>
                <div>
                  <p className={`font-bold text-sm ${r.accent}`}>{r.label}</p>
                  <p className="text-gray-400 text-sm leading-relaxed mt-0.5">{r.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================== CTA FINALE ======================== */}
      <section className="py-16 bg-[#0a0e1a] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-black text-white font-google mb-3">
            Pronto a fare il <span className="text-[#4285F4]">passo</span>?
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Scrivimi su WhatsApp o prenota una videochiamata gratuita. Ti ascolto, ti consiglio, e decidiamo insieme.
          </p>

          {/* Mini bundle reminder */}
          <div className="relative p-[2px] rounded-xl bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] mb-8 max-w-md mx-auto">
            <div className="bg-[#0c1220] rounded-[10px] px-5 py-3">
              <p className="text-white text-sm font-bold">
                Start = <span className="text-[#EA4335]">€199</span> · Plus = <span className="text-[#FBBC04]">€149</span> · Rooftop = <span className="text-[#34A853]">IN REGALO</span>
              </p>
              <p className="text-gray-500 text-xs mt-1">
                <PayPalLogo className="w-4 h-3 inline mr-1 text-gray-400" />
                3 rate PayPal · TAEG 0% · 100% Deducibile
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-full px-10 font-bold text-base"
              onClick={() => window.open(WA_LINK + '?text=' + encodeURIComponent('Ciao Riccardo! Voglio un pacchetto + formazione 100% deducibile!'), '_blank')}
              data-testid="cta-wa-bottom">
              <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp
            </Button>
            <Button size="lg" className="bg-[#4285F4] hover:bg-[#3574d4] text-white rounded-full px-10 font-bold text-base"
              onClick={() => window.open(CALENDLY, '_blank')} data-testid="cta-calendly-bottom">
              <Clock className="w-5 h-5 mr-2" /> Prenota Videochiamata
            </Button>
          </div>
        </div>
      </section>

      <SectionDivider />
      <CrossSellingGift />
      <CrossSellingProducts />
      <ContactSection />
    </div>
  );
};

export default DeducibilePage;
